/**
 * Shared Sentry initialization for web and plugin.
 * DSN check, BrowserTracing, breadcrumbs, replay, and sensitive-data filtering.
 */

import * as Sentry from '@sentry/react';

export interface SentryInitOptions {
  dsn: string | undefined;
  environment: string;
  release: string;
  application: string;
  page: string;
}

/**
 * Filter sensitive keys in breadcrumb body/headers (fetch/xhr).
 */
function filterSensitiveBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
    if (breadcrumb.data) {
      if (breadcrumb.data.body) {
        try {
          const body =
            typeof breadcrumb.data.body === 'string'
              ? JSON.parse(breadcrumb.data.body)
              : breadcrumb.data.body;
          if (typeof body === 'object' && body !== null) {
            const filteredBody: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(body)) {
              const lowerKey = key.toLowerCase();
              if (
                lowerKey.includes('password') ||
                lowerKey.includes('token') ||
                lowerKey.includes('auth') ||
                lowerKey.includes('apiKey')
              ) {
                filteredBody[key] = '[Filtered]';
              } else {
                filteredBody[key] = value;
              }
            }
            breadcrumb.data.body = JSON.stringify(filteredBody);
          }
        } catch {
          const bodyStr = String(breadcrumb.data.body);
          if (bodyStr.includes('password') || bodyStr.includes('token')) {
            breadcrumb.data.body = '[Filtered]';
          }
        }
      }
      if (breadcrumb.data.headers) {
        const headers = breadcrumb.data.headers as Record<string, unknown>;
        const filteredHeaders: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(headers)) {
          const lowerKey = key.toLowerCase();
          if (
            lowerKey.includes('authorization') ||
            lowerKey.includes('token') ||
            lowerKey.includes('auth')
          ) {
            filteredHeaders[key] = '[Filtered]';
          } else {
            filteredHeaders[key] = value;
          }
        }
        breadcrumb.data.headers = filteredHeaders;
      }
    }
  }
  return breadcrumb;
}

/**
 * Filter sensitive data from Sentry error events (request URL, cookies, message).
 */
function filterSensitiveEvent(event: Sentry.Event): Sentry.Event {
  if (event.request) {
    if (event.request.url) {
      try {
        const url = new URL(event.request.url);
        const sensitiveParams = ['token', 'key', 'apiKey', 'auth', 'password'];
        sensitiveParams.forEach((param) => {
          if (url.searchParams.has(param)) {
            url.searchParams.set(param, '[Filtered]');
          }
        });
        event.request.url = url.toString();
      } catch {
        // Invalid URL, skip
      }
    }
    if (event.request.cookies) {
      const filteredCookies: Record<string, string> = {};
      for (const [key, value] of Object.entries(event.request.cookies)) {
        const lowerKey = key.toLowerCase();
        const strValue = typeof value === 'string' ? value : String(value);
        if (
          lowerKey.includes('token') ||
          lowerKey.includes('auth') ||
          lowerKey.includes('session')
        ) {
          filteredCookies[key] = '[Filtered]';
        } else {
          filteredCookies[key] = strValue;
        }
      }
      event.request.cookies = filteredCookies;
    }
  }
  if (event.message) {
    event.message = event.message.replace(/password[=:]\s*[^\s,}]+/gi, 'password=[Filtered]');
    event.message = event.message.replace(/token[=:]\s*[^\s,}]+/gi, 'token=[Filtered]');
    event.message = event.message.replace(/api[_-]?key[=:]\s*[^\s,}]+/gi, 'api_key=[Filtered]');
  }
  return event;
}

/**
 * Initialize Sentry with shared config: DSN check, integrations, replay, and sensitive-data filtering.
 * Call from web or plugin with app-specific release and application/page tags.
 */
export function initSentryBase(options: SentryInitOptions): void {
  const { dsn, environment, release, application, page } = options;

  if (!dsn || dsn.includes('YOUR_') || dsn.includes('_HERE') || dsn.includes('NONE_FOR')) {
    console.warn('[Sentry] DSN not configured. Skipping Sentry initialization.');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [
      new Sentry.BrowserTracing({
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/.*\.cloudfunctions\.net/,
          /^https:\/\/.*\.firebaseapp\.com/,
        ],
      }),
      Sentry.breadcrumbsIntegration({
        console: false,
        dom: false,
        fetch: true,
        xhr: true,
        history: true,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: environment === 'development' ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeBreadcrumb(breadcrumb) {
      return filterSensitiveBreadcrumb(breadcrumb);
    },
    beforeSend(event) {
      return filterSensitiveEvent(event);
    },
  });

  Sentry.setTag('application', application);
  Sentry.setTag('page', page);
}
